import React from "react";
import { useLang } from "../../contexts/LanguageContext";
import { IMAGES } from "../../lib/i18n";

export default function Hero() {
  const { t } = useLang();

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      data-testid="hero-section"
      className="relative pt-44 pb-20 sm:pt-52 sm:pb-24 overflow-hidden bg-white"
    >
      <div
        aria-hidden
        className="hidden lg:block absolute -right-12 top-44 vertical-rl font-serif italic text-[12rem] leading-none text-olive/[0.06] select-none"
      >
        horizonte
      </div>

      <div className="mx-auto max-w-[1500px] px-5 sm:px-8 grid grid-cols-12 gap-y-12 gap-x-6">
        <div className="col-span-12 lg:col-span-7 flex items-start gap-4 animate-fade-up">
          <span className="block h-px w-12 bg-terracotta mt-3" aria-hidden />
          <span className="eyebrow">{t.hero.eyebrow}</span>
        </div>

        <h1
          data-testid="hero-headline"
          className="col-span-12 lg:col-span-7 lg:col-start-1 h-hero text-olive animate-fade-up"
          style={{ animationDelay: "120ms" }}
        >
          {t.hero.titleA}
          <span className="italic text-terracotta"> {t.hero.titleB} </span>
          {t.hero.titleC}{" "}
          <span className="font-light italic">{t.hero.titleD}</span>
        </h1>

        <div
          className="col-span-12 lg:col-span-5 lg:col-start-8 lg:-mt-44 relative animate-fade-up"
          style={{ animationDelay: "260ms" }}
        >
          <div className="aspect-[3/4] overflow-hidden">
            <img
              src={IMAGES.hero}
              alt={t.hero.captions.imageAlt}
              className="w-full h-full object-cover"
              loading="eager"
            />
          </div>
          <div className="absolute -bottom-5 -left-5 sm:left-auto sm:-right-5 bg-ivory border border-border px-5 py-4 max-w-[230px]">
            <div className="font-mono text-[10px] tracking-[0.35em] uppercase text-olive-soft">
              {t.hero.captions.badgeLabel}
            </div>
            <div className="font-serif italic text-2xl text-olive mt-1">{t.hero.captions.badgeValue}</div>
          </div>
        </div>

        <div
          className="col-span-12 lg:col-span-7 lg:col-start-1 lg:row-start-3 animate-fade-up"
          style={{ animationDelay: "320ms" }}
        >
          <p className="font-serif text-2xl sm:text-3xl text-olive leading-snug max-w-xl mb-6">
            {t.hero.lede}
          </p>
          <p className="text-base sm:text-lg leading-relaxed text-olive-soft max-w-xl mb-10">
            {t.hero.pitch}
          </p>
          <div className="flex flex-wrap items-center gap-4">
            <button
              onClick={() => scrollTo("lanzadera")}
              data-testid="hero-cta-primary"
              className="bg-olive text-ivory px-7 py-4 font-mono text-[11px] tracking-[0.28em] uppercase hover:bg-terracotta transition-all duration-500 hover:tracking-[0.32em]"
            >
              {t.hero.ctaPrimary}
            </button>
            <button
              onClick={() => scrollTo("documentacion")}
              data-testid="hero-cta-secondary"
              className="px-7 py-4 font-mono text-[11px] tracking-[0.28em] uppercase border border-olive/40 text-olive hover:border-olive transition-all duration-500"
            >
              {t.hero.ctaSecondary}
            </button>
          </div>
        </div>

        <div className="col-span-12 lg:row-start-4 mt-14 border-t border-border pt-8 grid grid-cols-2 sm:grid-cols-4 gap-6 sm:gap-10 animate-fade-up" style={{ animationDelay: "420ms" }}>
          {t.hero.kpis.map((k, i) => (
            <div key={i} data-testid={`hero-kpi-${i}`} className="flex flex-col gap-1">
              <div className="font-mono text-[10px] tracking-[0.35em] uppercase text-olive-soft">
                {k.label}
              </div>
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="font-serif text-4xl sm:text-5xl text-olive">{k.value}</span>
                <span className="font-mono text-xs uppercase tracking-[0.2em] text-terracotta">
                  {k.unit}
                </span>
              </div>
              {k.note && (
                <p className="text-sm text-olive-soft italic font-serif leading-snug mt-2 max-w-xs">{k.note}</p>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
